import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

import type { SortConfig, FilterValues } from '@/shared/components';
import type { NotificationList } from '../models';
import { useNotificationMutations } from '../queries';

export const useNotificationTableLogic = (
  customerID?: string,
  tenantId?: string | null
) => {
  const { useListNotifications } = useNotificationMutations();
  const navigate = useNavigate();
  type SortableNotificationField = 'title' | 'message' | 'createdAt';

  // ----------------------------
  // State initialization
  // ----------------------------
  const getFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      ...(params.get('searchTerm')
        ? { search: params.get('searchTerm')! }
        : {}),
    };
  };

  const getPageStateFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      page: parseInt(params.get('page') ?? '1'),
      limit: parseInt(params.get('limit') ?? '10'),
    };
  };

  const getSortConfigFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get('sortBy') as SortableNotificationField | null;
    const sortOrder = params.get('sortOrder') as 'asc' | 'desc' | null;
    return sortBy && sortOrder ? { field: sortBy, direction: sortOrder } : null;
  };

  // ----------------------------
  // State
  // ----------------------------
  const [mounted, setMounted] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(getFiltersFromUrl);
  const [pageState, setPageState] = useState(getPageStateFromUrl);
  const [sortConfig, setSortConfig] = useState<
    (SortConfig & { field: SortableNotificationField }) | null
  >(getSortConfigFromUrl);

  // ----------------------------
  // Api call
  // ----------------------------
  const { data, isLoading, error } = useListNotifications({
    page: pageState.page,
    limit: pageState.limit,
    searchTerm: currentFilters.search,
    sortBy: sortConfig?.field,
    sortOrder: sortConfig?.direction,
    customerID: customerID,
    ...(tenantId ? { tenantId } : {}),
  });

  // ----------------------------
  // Mount effect
  // ----------------------------
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;

    const params = new URLSearchParams();
    if (currentFilters.search) params.set('searchTerm', currentFilters.search);
    params.set('page', String(pageState.page));
    params.set('limit', String(pageState.limit));
    if (sortConfig?.field) {
      params.set('sortBy', sortConfig.field);
      params.set('sortOrder', sortConfig.direction);
    }

    const newUrl = window.location.pathname + '?' + params.toString();
    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [mounted, currentFilters, pageState, sortConfig]);

  // ----------------------------
  // Sync URL on back navigation
  // ----------------------------
  useEffect(() => {
    const handlePopState = () => {
      setPageState(getPageStateFromUrl());
      setCurrentFilters(getFiltersFromUrl());
      setSortConfig(getSortConfigFromUrl());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ----------------------------
  // Click Handlers
  // ----------------------------
  const handlePageChange = useCallback((page: number) => {
    setPageState(prev => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((limit: number) => {
    setPageState({ page: 1, limit });
  }, []);

  const handleSortChange = useCallback((sort: SortConfig | null) => {
    if (sort) {
      const field = (sort.field.charAt(0).toLowerCase() +
        sort.field.slice(1)) as SortableNotificationField;
      setSortConfig({ ...sort, field });
    } else {
      setSortConfig(null);
    }
  }, []);

  const handleFilterChange = useCallback((filters: FilterValues) => {
    setCurrentFilters(prev => {
      const changed = JSON.stringify(prev) !== JSON.stringify(filters);
      if (changed) {
        setPageState(p => ({ ...p, page: 1 }));
      }
      return filters;
    });
  }, []);

  const handleView = useCallback(
    (user: NotificationList) => {
      navigate({
        to: '/notifications/$Id',
        params: { Id: user.Id },
      });
    },
    [navigate]
  );

  const pagination = {
    page: data?.Page ?? 0,
    pageSize: data?.Limit ?? 0,
    total: data?.TotalCount ?? 0,
    totalPages: data?.TotalPageCount ?? 0,
  };

  return {
    users: data?.data ?? [],
    loading: isLoading,
    error,
    pagination,
    currentFilters,
    onFilterChange: handleFilterChange,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onSortChange: handleSortChange,
    onView: handleView,
  };
};
